import { useMemo, useState } from "react";
import { ReelTile } from "@/components/site/case/ReelTile";
import { PhotoLightbox } from "@/components/site/foto/PhotoLightbox";
import { galleryPhotos } from "@/content/photo-gallery";
import type { ReelItem } from "@/content/work-reel";

type Row = { kind: "featureLeft" | "trio" | "pair" | "featureRight"; items: ReelItem[] };

const PATTERN: { kind: Row["kind"]; size: number }[] = [
  { kind: "featureLeft", size: 3 },
  { kind: "trio", size: 3 },
  { kind: "pair", size: 2 },
  { kind: "featureRight", size: 3 },
];

function buildRows(items: ReelItem[]): Row[] {
  const rows: Row[] = [];
  let i = 0;
  let p = 0;
  while (i < items.length) {
    const step = PATTERN[p % PATTERN.length]!;
    const slice = items.slice(i, i + step.size);
    if (slice.length === 0) break;
    rows.push({ kind: slice.length < step.size ? "trio" : step.kind, items: slice });
    i += slice.length;
    p += 1;
  }
  return rows;
}

/**
 * Kuraterad mosaik för arbetsreelen: rader som växlar mellan en stor ruta med
 * två mindre bredvid, trio och par. Ger rytm i stället för ett platt rutnät.
 */
export function ReelMosaic({
  items,
  limit,
  className,
}: {
  items: ReelItem[];
  limit?: number;
  className?: string;
}) {
  const [shown, setShown] = useState(limit ?? items.length);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const visible = items.slice(0, shown);
  const rows = useMemo(() => buildRows(visible), [visible.length, items]);

  const openPhoto = (id: string) => {
    const src = items.find((it) => it.id === id && it.kind === "photo");
    if (!src || src.kind !== "photo") return;
    const idx = galleryPhotos.findIndex((p) => `photo-${p.id}` === id);
    if (idx >= 0) setLightbox(idx);
  };

  return (
    <div className={className}>
      <div className="space-y-4 md:space-y-5">
        {rows.map((row, ri) => {
          const eager = ri === 0;
          const hasPhoto = row.items.some((it) => it.kind === "photo");
          // Foto får aldrig beskäras eller letterboxas: rader med bild renderas
          // som ett vanligt rutnät där varje ruta följer bildens proportioner.
          if (hasPhoto) {
            return (
              <div
                key={ri}
                className="grid items-start gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-5"
              >
                {row.items.map((it) => (
                  <ReelTile key={it.id} item={it} eager={eager} onPhotoClick={openPhoto} />
                ))}
              </div>
            );
          }
          if (row.kind === "trio" || row.items.length < 3) {
            return (
              <div key={ri} className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-5">
                {row.items.map((it) => (
                  <ReelTile key={it.id} item={it} eager={eager} onPhotoClick={openPhoto} />
                ))}
              </div>
            );
          }
          if (row.kind === "pair") {
            return (
              <div key={ri} className="grid gap-4 md:grid-cols-2 md:gap-5">
                {row.items.map((it) => (
                  <ReelTile key={it.id} item={it} eager={eager} onPhotoClick={openPhoto} />
                ))}
              </div>
            );
          }
          const [a, b, c] = row.items;
          const feature = row.kind === "featureLeft" ? a! : c!;
          const rest = row.kind === "featureLeft" ? [b!, c!] : [a!, b!];
          const featureFirst = row.kind === "featureLeft";
          return (
            <div
              key={ri}
              className="grid gap-4 md:h-[460px] md:grid-cols-3 md:grid-rows-1 md:gap-5 lg:h-[520px]"
            >
              <div
                className={
                  featureFirst
                    ? "md:col-span-2 md:h-full md:min-h-0"
                    : "md:order-2 md:col-span-2 md:h-full md:min-h-0"
                }
              >
                <ReelTile item={feature} fill eager={eager} onPhotoClick={openPhoto} />
              </div>
              <div
                className={
                  featureFirst
                    ? "grid gap-4 sm:grid-cols-2 md:h-full md:min-h-0 md:grid-cols-1 md:grid-rows-2 md:gap-5"
                    : "grid gap-4 sm:grid-cols-2 md:order-1 md:h-full md:min-h-0 md:grid-cols-1 md:grid-rows-2 md:gap-5"
                }
              >
                {rest.map((it) => (
                  <ReelTile
                    key={it.id}
                    item={it}
                    aspect="square"
                    fill
                    eager={eager}
                    onPhotoClick={openPhoto}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {shown < items.length ? (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => setShown((s) => s + (limit ?? 12))}
            className="rounded-full border border-white/20 px-6 py-3 text-sm uppercase tracking-widest text-foreground/85 transition-colors hover:border-mint hover:text-mint"
          >
            Visa fler ({items.length - shown} kvar)
          </button>
        </div>
      ) : null}

      <PhotoLightbox
        photos={galleryPhotos}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onIndexChange={setLightbox}
      />
    </div>
  );
}
